import type {
  DraggableAttributes,
  DraggableSyntheticListeners,
} from "@dnd-kit/core";
import { CircularProgress, Menu, MenuItem } from "@mui/material";
import clsx from "clsx";
import { CSSProperties, FC, MouseEvent, ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSetRecoilState } from "recoil";

import { PIC, SVG_MUI } from "@ryde/assets";
import { MuiButton } from "@ryde/controls";
import { useModalConfirm } from "@ryde/redux";
import {
  buildCardFiltersSearch,
  ConfirmResult,
  hasKeys,
  Log,
  usePrefixedRouting,
} from "@ryde/shared";

import {
  dashboardCardsAtom,
  dashboardEditorAtom,
} from "../dynamic-dashboard.atoms";
import { DASHBOARD_CARD_APPEAR_VARIANTS } from "../dynamic-dashboard.constants";
import {
  DashboardChartType,
  IDashboardCard,
} from "../dynamic-dashboard.models";
import { DashboardCardStyled } from "../dynamic-dashboard.styled";
import { buildCardLinkFilters } from "../dynamic-dashboard.system-cards.utils";
import { useDashboardCardData } from "../hooks/use-dashboard-card-data";
import {
  DashboardBarChart,
  DashboardLineChart,
  DashboardMultiSeriesBarChart,
  DashboardPieChart,
  DashboardStatCard,
} from "./charts";
import { DashboardLegacyCardBody } from "./dashboard-legacy-card";
import { DashboardCardFilterBadge } from "./dynamic-dashboard-card.filter-badge";

interface IDynamicDashboardCardProps {
  card: IDashboardCard;
  /** dnd-kit sortable wiring (provided by DynamicDashboardSortableCard). */
  setNodeRef?: (node: HTMLElement | null) => void;
  style?: CSSProperties;
  attributes?: DraggableAttributes;
  listeners?: DraggableSyntheticListeners;
  isDragging?: boolean;
  /** True when a dragged card is currently hovering this one (drop target). */
  isOver?: boolean;
  /**
   * The drag-overlay copy: render a static placeholder body, NOT the
   * data-driven chart — so dragging doesn't spin up a second
   * `useDashboardCardData` fetch for the cloned card.
   */
  isDragClone?: boolean;
  shouldAnimateAppear?: boolean;
}

/**
 * Report-card body — owns `useDashboardCardData` (the fetch) and renders the
 * chart / loading / permission / empty states. Split out from the card frame so
 * the parent can skip mounting it (the drag clone, a legacy card), avoiding a
 * redundant fetch.
 */
const DynamicDashboardCardBody: FC<{
  card: IDashboardCard;
  isDragging?: boolean;
}> = ({ card, isDragging }) => {
  const { t } = useTranslation();
  const {
    content,
    multiSeries,
    stat,
    dimensionLabel,
    metricLabel,
    status,
    valueFormatter,
    reload,
  } = useDashboardCardData(card);

  const renderChart = (): ReactNode => {
    switch (card.chartType) {
      case DashboardChartType.Pie:
        return (
          <DashboardPieChart
            content={content}
            valueFormatter={valueFormatter}
          />
        );
      case DashboardChartType.Line:
        return (
          <DashboardLineChart
            content={content}
            metricLabel={metricLabel}
            dimensionLabel={dimensionLabel}
            valueFormatter={valueFormatter}
          />
        );
      case DashboardChartType.HorizontalBar:
        return (
          <DashboardBarChart
            orientation="horizontal"
            content={content}
            metricLabel={metricLabel}
            dimensionLabel={dimensionLabel}
            valueFormatter={valueFormatter}
          />
        );
      case DashboardChartType.Clustered:
        return (
          <DashboardMultiSeriesBarChart
            multiSeries={multiSeries}
            metricLabel={metricLabel}
            dimensionLabel={dimensionLabel}
            valueFormatter={valueFormatter}
          />
        );
      case DashboardChartType.Stacked:
        return (
          <DashboardMultiSeriesBarChart
            multiSeries={multiSeries}
            metricLabel={metricLabel}
            dimensionLabel={dimensionLabel}
            valueFormatter={valueFormatter}
            stacked
          />
        );
      case DashboardChartType.Stat:
        return stat ? (
          <DashboardStatCard {...stat} metricLabel={metricLabel} />
        ) : null;
      case DashboardChartType.VerticalBar:
      default:
        return (
          <DashboardBarChart
            orientation="vertical"
            content={content}
            metricLabel={metricLabel}
            dimensionLabel={dimensionLabel}
            valueFormatter={valueFormatter}
          />
        );
    }
  };

  // While the card is being dragged, show an empty body — the live chart canvas
  // mis-measures mid-drag and can render huge. The hook stays mounted so drag
  // end doesn't refetch.
  if (isDragging) {
    return <div className="dashboard-card-empty" />;
  }
  if (status.loading) {
    return (
      <div className="dashboard-card-empty">
        <CircularProgress size={22} />
      </div>
    );
  }
  if (status.forbidden) {
    return (
      <div className="dashboard-card-empty">
        {t("dashboard.dynamic.noAccess")}
      </div>
    );
  }
  if (status.metricDenied) {
    return (
      <div className="dashboard-card-empty">
        {t("dashboard.dynamic.metricDenied")}
      </div>
    );
  }
  if (status.error) {
    return (
      <div className="dashboard-card-empty">
        <span>{t("dashboard.dynamic.loadError")}</span>
        <MuiButton
          muiVariant="text"
          e2eData="dashboard-card-retry"
          onClick={reload}
        >
          {t("dashboard.dynamic.retry")}
        </MuiButton>
      </div>
    );
  }
  if (status.isEmpty) {
    return (
      <div className="dashboard-card-empty">
        {t("dashboard.dynamic.noData")}
      </div>
    );
  }
  return renderChart();
};

/**
 * Presentational dashboard card. Frame styled like the Rides card, with the
 * 6-dot grip as the drag activator, a title, card buttons, and a chart body
 * rendered by `DynamicDashboardCardBody` matching the card's chart type.
 */
export const DynamicDashboardCard: FC<IDynamicDashboardCardProps> = ({
  card,
  setNodeRef,
  style,
  attributes,
  listeners,
  isDragging,
  isOver,
  isDragClone,
  shouldAnimateAppear = true,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { addPrefix } = usePrefixedRouting();
  const { confirmDialogResult } = useModalConfirm();
  const setEditor = useSetRecoilState(dashboardEditorAtom);
  const setCards = useSetRecoilState(dashboardCardsAtom);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const isLegacy =
    card.chartType === DashboardChartType.Legacy &&
    Boolean(card.legacyCardName);
  const displayTitle =
    isLegacy && card.legacyCardName
      ? t(`dashboard.${card.legacyCardName}.TITLE`)
      : card.title;

  // Carries the card's filters to the target grid as field names; that page
  // resolves them to its own control ids on arrival, since the dashboard has no
  // grid schema of its own to resolve them here. No filters → a plain link.
  const handleOpen = (): void => {
    if (!card.linkTo) {
      return;
    }

    const { filters, fixedFilters, unmappedKeys } = buildCardLinkFilters(card);
    if (unmappedKeys.length) {
      Log.warn("Card link dropped its narrowing, no field on target grid", {
        cardName: card.legacyCardName,
        linkTo: card.linkTo,
        unmappedKeys,
      });
      // The user's narrowing can't be expressed, but the card's OWN filters can:
      // keep those so the page still shows the card's population (open feedback,
      // rates priced 0) rather than the entire table.
      navigate(
        hasKeys(fixedFilters)
          ? `${addPrefix(card.linkTo)}${buildCardFiltersSearch(fixedFilters)}`
          : addPrefix(card.linkTo),
      );
      return;
    }

    navigate(`${addPrefix(card.linkTo)}${buildCardFiltersSearch(filters)}`);
  };

  const openMenu = (event: MouseEvent<HTMLElement>): void => {
    setMenuAnchor(event.currentTarget);
  };

  const closeMenu = (): void => {
    setMenuAnchor(null);
  };

  const handleEdit = (): void => {
    closeMenu();
    setEditor({ isOpen: true, cardId: card.id });
  };

  // Delete goes through the app-wide confirm dialog (`useModalConfirm`); the
  // card is removed only on an explicit OK, otherwise everything stays as-is.
  const handleDelete = async (): Promise<void> => {
    closeMenu();
    const result = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message: t("general.ARE_YOU_SURE"),
    });
    if (result !== ConfirmResult.OK) {
      return;
    }
    setCards(prev => prev.filter(c => c.id !== card.id));
  };

  const renderBody = (): ReactNode => {
    // The drag clone is a static preview — no data hook, no fetch.
    if (isDragClone) {
      return <div className="dashboard-card-empty" />;
    }
    if (isLegacy && card.legacyCardName) {
      return (
        <DashboardLegacyCardBody
          cardName={card.legacyCardName}
          cardId={card.id}
        />
      );
    }
    return <DynamicDashboardCardBody card={card} isDragging={isDragging} />;
  };

  return (
    <DashboardCardStyled
      ref={setNodeRef}
      style={style}
      $isDragging={isDragging}
      $isOver={isOver}
      className={clsx({ wide: card.size === "wide" })}
      variants={
        shouldAnimateAppear ? DASHBOARD_CARD_APPEAR_VARIANTS : undefined
      }
      initial={shouldAnimateAppear ? undefined : false}
      {...attributes}
    >
      <div className="dashboard-card-header">
        <button
          className="grip"
          type="button"
          aria-label="Move card"
          {...listeners}
        >
          <PIC.SixDotsSvg />
        </button>

        <div className="titles">
          <span className="title">{displayTitle}</span>
        </div>

        <span className="spacer" />

        {!isDragClone && (
          <DashboardCardFilterBadge card={card} onClick={handleEdit} />
        )}
        {card.linkTo && (
          <button
            className="icon-btn"
            type="button"
            aria-label="Open page"
            onClick={handleOpen}
          >
            <PIC.Lauch />
          </button>
        )}
        <button
          className="icon-btn"
          type="button"
          aria-label="Card actions"
          aria-haspopup="true"
          onClick={openMenu}
        >
          <SVG_MUI.EllipsisVertical3SVG />
        </button>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={closeMenu}
        >
          <MenuItem onClick={handleEdit}>{t("general.EDIT")}</MenuItem>
          <MenuItem
            onClick={() => {
              handleDelete();
            }}
          >
            {t("general.ARCHIVE")}
          </MenuItem>
        </Menu>
      </div>

      <div className="dashboard-card-body">{renderBody()}</div>
    </DashboardCardStyled>
  );
};
